import Styles from './PublicAccess.module.scss'
import { ShareSheetScreen, useShareSheetContext } from '../ShareSheetV2'
import { Heading } from 'blaze-ui/Heading/Heading'
import useShareSheetV2 from 'hooks/useShareSheetV2'
import { Permission, UserRole } from 'types'
import { ReactText, useEffect, useState } from 'react'
import Eye from 'almanac-ui/icons/16/Eye'
import { AccessDropdown } from 'components/ShareSheet/Access/AccessDropdown'
import { ReactComponent as LockIcon } from 'assets/icons/locked16.svg'
import { ReactComponent as Trash } from 'assets/icons/trash16.svg'
import { PasswordInput } from '../PasswordInput/PasswordInput'
import cx from 'classnames'
import { Modal } from 'almanac-ui/Modal'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { Button } from 'blaze-ui/Button'
import { AbilityContext } from 'components/Can'
import { useAbility } from '@casl/react'
import { SubjectType } from 'utils/caslAbility'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { AssetType } from 'hooks/useAlmanacFileSubscription'

export const PublicAccess = observer(() => {
  const { files } = useRootStore()
  const { setScreen, file } = useShareSheetContext()
  const { publicAccess, inheritedPublicAccess, createPublicAccess, removePublicAccess, selectedPermission } =
    useShareSheetV2(file)
  const [deletingAccess, setDeletingAccess] = useState(false)
  const workspace = useCurrentWorkspace()
  const ability = useAbility(AbilityContext)
  const almanacFile = files.get(file.id)

  useEffect(() => {
    files.fetch(file.id)
  }, [file.id, files])

  const authedFile = {
    role: workspace?.role || UserRole.UNAUTHED,
    permissions: almanacFile?.permissions,
    __caslSubjectType__: SubjectType.DESIGN,
  }

  const can: typeof ability.can = (action, subject) => {
    return ability.can(action, subject)
  }

  const updatePublicAccess = (permission: Permission | null, password?: string | null) => {
    const onError = () => {}
    if (!!permission) {
      createPublicAccess(permission, onError, password)
      return
    }

    if (!!publicAccess) {
      setDeletingAccess(true)
      removePublicAccess(publicAccess, onError)
      setDeletingAccess(false)
    }
  }
  return (
    <div className={Styles.container}>
      <ResponsiveDesign.Desktop>
        <div className={Styles.header}>
          <div className={Styles.headerLeft}>
            <Modal.BackButton onPress={() => setScreen(ShareSheetScreen.Default)} />
            <Heading level={3}>Share by Link</Heading>
          </div>
        </div>
      </ResponsiveDesign.Desktop>
      <div className={Styles.body}>
        <div className={Styles.accessLevel}>
          <Heading level={5}>
            {!!publicAccess ? 'This file is publicly accessible' : 'Make this file publicly accessible'}
          </Heading>
          <Button
            variant={publicAccess ? 'secondary' : 'primary'}
            size="md"
            isDisabled={!!inheritedPublicAccess || deletingAccess || !can('edit', authedFile)}
            onPress={() => updatePublicAccess(publicAccess ? null : Permission.Edit)}
          >
            {publicAccess ? 'Unpublish' : 'Publish'}
          </Button>
        </div>
        <div className={cx(Styles.publicAccessContainer, { [Styles.disabled]: !publicAccess })}>
          <div className={Styles.accessLevel}>
            <div className={Styles.accessLevelLeft}>
              <Eye color={Styles.grey40} />
              <span className={Styles.accessText}>Anyone with the link can…</span>
            </div>
            <AccessDropdown
              options={
                almanacFile?.asset_type === AssetType.Design
                  ? [Permission.Edit, Permission.Read]
                  : [Permission.Edit, Permission.Comment, Permission.Read]
              }
              currentPermission={publicAccess?.access || Permission.Edit}
              size="sm"
              disabled={!publicAccess}
              handleClick={(permission: ReactText) =>
                updatePublicAccess(permission as Permission, publicAccess?.password)
              }
            />
          </div>
          {almanacFile?.asset_type !== AssetType.Design && (
            <div className={Styles.passwordContainer}>
              <div className={Styles.passwordLeft}>
                <div className={Styles.lockIcon}>
                  <LockIcon />
                </div>
                <div>Password</div>
              </div>
              <div className={Styles.passwordRight}>
                <PasswordInput file={file} />
                {publicAccess && !!publicAccess.password && (
                  <Button variant="tertiary" size="lg" onPress={() => updatePublicAccess(selectedPermission, null)}>
                    <Trash />
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
})
